import SolutionsCaseStudyCTA from '@web/sections/Solutions/CaseStudyCTA'
import SolutionsChallenges from '@web/sections/Solutions/Challenges'
import SolutionsFooter from '@web/sections/Solutions/Footer'
import SolutionsHeader from '@web/sections/Solutions/Header'
import OtherSectors from '@web/sections/Solutions/OtherSectors'
import SolutionsRegulatoryContext from '@web/sections/Solutions/RegulatoryContext'
import SolutionsUseCases from '@web/sections/Solutions/UseCases'

export function EmergencyPlanningPage() {
  return (
    <>
      <SolutionsHeader
        header="Emergency planning and business continuity through Resilience Explorer®"
        arcadeLink={{
          src: 'https://demo.arcade.software/rwp0tcNflT8uxaFe2wqA?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
          title: 'How Resilience Explorer® Enables Adaptation Planning',
        }}
        subheader="The benefit of hindsight, beforehand."
        descriptionLeft={
          <>
            <p>
              Resilience Explorer® helps you understand how climate and natural hazard impacts
              cascade across infrastructure networks and communities. It provides the evidence to
              support emergency planning, readiness exercises, and business continuity planning with
              realistic, forward-looking scenarios.
            </p>
          </>
        }
        descriptionRight={
          <p>
            Effective emergency planning depends on understanding not just where a climate or
            natural hazard event will strike, but what will stop working as a result. The
            communities most at risk, the services most likely to fail, and the response capacity
            needed most: this information has to exist before the event, not after.
          </p>
        }
      />
      <SolutionsChallenges
        header="Challenges facing Emergency Planning and Business Continuity"
        sections={[
          {
            title:
              'Understanding how climate and natural hazard impacts may spread across infrastructure networks and services',
            listitems: [],
          },
          {
            title:
              'Identifying which communities may face isolation, service loss, or greater harm during an event',
            listitems: [],
          },
          {
            title:
              'Ensuring agencies share consistent information about risks, dependencies, and potential disruptions',
            listitems: [],
          },
        ]}
      />
      <SolutionsRegulatoryContext text="Emergency management legislation requires agencies to understand climate and natural hazard impacts and plan how essential services and communities will function during emergencies. Resilience Explorer® helps you do both." />
      <SolutionsUseCases
        header="How Resilience Explorer® Enables Emergency Planning and Business Continuity"
        image={{
          src: '/website/emergency_planning.jpg',
          alt: 'Emergency Planning and Business Continuity Header',
        }}
        accordions={[
          {
            title: 'Understand how hazards affect infrastructure networks',
            subtitle:
              'See how climate and natural hazard impacts may disrupt essential infrastructure across the network.',
            content: (
              <>
                <p>
                  The consequences of a climate or natural hazard event rarely stop at the first
                  point of impact. Disruptions compound across transport, electricity, water, and
                  other systems in ways that are difficult to anticipate without network-level
                  modelling.
                </p>
                <p className="mt-4">
                  Resilience Explorer® helps you see how climate and natural hazard events may
                  affect infrastructure and how those disruptions may spread across interdependent
                  systems.
                </p>
                <p className="mt-4">
                  This means your planning is based on a realistic picture of consequences, not just
                  the immediate point of failure.
                </p>
              </>
            ),
          },
          {
            title: 'Identify communities that may be most affected',
            subtitle:
              'Understand which communities may face the greatest disruption before an event occurs.',
            content: (
              <>
                <p>
                  Isolation, limited access routes, and reliance on vulnerable infrastructure
                  concentrate risk in specific places, but that is not always visible in standard
                  hazard mapping.
                </p>
                <p className="mt-4">
                  Resilience Explorer® overlays hazard exposure with network dependencies and social
                  vulnerability to identify which communities may lose access to services or require
                  additional support during a climate or natural hazard event.
                </p>
                <p className="mt-4">
                  This means response plans, pre-positioning decisions, and welfare arrangements
                  reflect where support will actually be needed.
                </p>
              </>
            ),
          },
          {
            title: 'Support emergency planning and readiness exercises',
            subtitle: 'Use realistic scenarios to strengthen preparedness.',
            content: (
              <>
                <p>
                  Plans that are tested against historical events may not hold up under the
                  conditions your community is likely to face in the future.
                </p>
                <p className="mt-4">
                  Resilience Explorer® lets you explore how climate and natural hazard events may
                  affect infrastructure and communities under different scenarios and return
                  periods, giving readiness exercises a more realistic and forward-looking
                  foundation.
                </p>
                <p className="mt-4">
                  This means gaps in plans are identified before an event reveals them, and
                  cross-agency coordination is built on shared assumptions.
                </p>
              </>
            ),
          },
          {
            title: 'Inform staging and resource planning',
            subtitle: 'Understand where response resources may be needed most.',
            content: (
              <>
                <p>
                  Pre-positioning personnel, equipment, and supplies effectively depends on knowing
                  where disruption is most likely and what the consequences will be.
                </p>
                <p className="mt-4">
                  Resilience Explorer® models likely access constraints and consequence patterns
                  under different event scenarios to help you determine optimal locations for
                  emergency pods, critical spares, and staging areas.
                </p>
                <p className="mt-4">
                  This means resource decisions are based on evidence rather than assumption, and
                  response capacity is where it needs to be when it is needed.
                </p>
              </>
            ),
          },
          {
            title: 'Support business continuity planning',
            subtitle:
              'Understand how infrastructure disruptions affect the services your organisation is responsible for.',
            content: (
              <>
                <p>
                  Continuity plans that do not account for infrastructure interdependencies may fail
                  under realistic conditions, when the impacts are not isolated to a single system.
                </p>
                <p className="mt-4">
                  Resilience Explorer® helps infrastructure operators and emergency planners
                  understand how service disruptions may occur during climate and natural hazard
                  events and how they may flow through to communities.
                </p>
                <p className="mt-4">
                  This means continuity plans are grounded in a realistic understanding of how your
                  services and networks actually behave under stress.
                </p>
              </>
            ),
          },
        ]}
      />
      <SolutionsCaseStudyCTA header="Strengthen emergency planning with clear evidence of how climate and natural hazard events affect infrastructure and communities." />
      <OtherSectors />
      <SolutionsFooter />
    </>
  )
}
